import { privateRequest } from "../axios";
import type { ApiResponse, ListApiResponse } from "@/types/common";
import type { UserCategory } from "./user";

// Confirmed against a real `GET /subscription-plan` response given
// directly by the user — this is the actual response shape, not a guess.
// Note: this model has no "status" (live/invite-only) or "memberCount"
// field — see pages/Subscriptions/components/PlanCard.tsx for how the UI
// adapts to that gap. `description` carries its own embedded newlines and
// checkmarks (e.g. "WHAT IT INCLUDES\n\n✓ ...") — there is no separate
// features array. `appleProductId`/`razorpayPlanId` are `string | null` —
// a later multi-row sample (AddMemberModal.tsx's membership-tier field)
// showed both `null` on plans with no app-store/Razorpay integration set
// up yet, not just populated strings as the original single-plan sample
// suggested.
export interface SubscriptionPlan {
  id: number;
  name: string;
  amount: number;
  planDurationMonth: number;
  description: string;
  tag: string;
  appleProductId: string | null;
  razorpayPlanId: string | null;
  createdBy: string;
  updatedBy: string;
  updatedAt: string;
  createdAt: string;
  totalActiveMemberships: number;
  annualRecurringRevenue: number;
  // Sent on create/update (see the payloads below). Optional here since
  // it isn't in the confirmed list response above — used to prefill the
  // edit form when present.
  userCategory?: UserCategory | null;
}

export interface GetSubscriptionPlansParams {
  search?: string;
  pageNo?: number;
  perPage?: number;
  category?: UserCategory;
}

// Real `POST /admin/subscription-plan` request body, confirmed against a
// real payload the user tested successfully — narrower than the full
// Swagger example: no `id`, `appleProductId`, or `razorpayPlanId`, and
// `amount` is sent as a string, not a number.
export interface CreateSubscriptionPlanPayload {
  name: string;
  amount: string;
  planDurationMonth: number;
  description: string;
  tag: string;
  userCategory: UserCategory;
}

// Editing reuses the exact same `POST /subscription-plan` endpoint as
// create — same convention as venues' `updateVenue`
// (services/api/venues.ts) — with a real `id` added. Confirmed against a
// real payload the user tested successfully; unlike create, `amount` is a
// number here, not a string.
export interface UpdateSubscriptionPlanPayload {
  id: number;
  name: string;
  amount: number;
  planDurationMonth: number;
  description: string;
  tag: string;
  userCategory: UserCategory;
}

const ENDPOINTS = {
  SUBSCRIPTION_PLAN: "/subscription-plan",
};

const getSubscriptionPlans = async (params?: GetSubscriptionPlansParams) => {
  const response = await privateRequest.get<ListApiResponse<SubscriptionPlan>>(ENDPOINTS.SUBSCRIPTION_PLAN, { params });
  return response.data;
};

const createSubscriptionPlan = async (payload: CreateSubscriptionPlanPayload) => {
  const response = await privateRequest.post<ApiResponse<SubscriptionPlan>>(ENDPOINTS.SUBSCRIPTION_PLAN, payload);
  return response.data;
};

const updateSubscriptionPlan = async (payload: UpdateSubscriptionPlanPayload) => {
  const response = await privateRequest.post<ApiResponse<SubscriptionPlan>>(ENDPOINTS.SUBSCRIPTION_PLAN, payload);
  return response.data;
};

// `DELETE /subscription-plan/{id}` — not confirmed against the real API
// yet; follows the same `/{resource}/{id}` delete convention as venues/
// questions/users. If the real endpoint differs, fix it here.
const deleteSubscriptionPlan = async (planId: number) => {
  const response = await privateRequest.delete<ApiResponse<unknown>>(`${ENDPOINTS.SUBSCRIPTION_PLAN}/${planId}`);
  return response.data;
};

const subscriptionService = {
  getSubscriptionPlans,
  createSubscriptionPlan,
  updateSubscriptionPlan,
  deleteSubscriptionPlan,
};

export default subscriptionService;
